import { IconName } from '@shared/components/core/images/IconMap';
import { getSupportedLocale } from '@shared/hooks/helpers/LocalizationHelpers';
import KeyCommandMapping, { KeyCommandDisplayMapping } from '@shared/models/deviceProfiles/KeyCommandMapping';
import { OSTypes } from '@shared/models/devices/OSTypes';
import { DisplayLocales } from '@shared/models/localization/Locales';
import { GetCommandType } from './CommandValues';

// TODO update to add isModifier to KeyCommandDisplayMapping and use that here
// TODO update to denote key source (keyboard, mouse, gamepad, etc) for filtering
export const KeyBindingViewModel = (
    key: string,
    displayName: string,
    icon: IconName = 'keySpace',
    defaultCommand?: string,
    alternateKeys: string[] = [],
    platform: OSTypes = 'generic'
) =>
    ({
        commands: [{ command: defaultCommand ?? key, commandType: GetCommandType(defaultCommand ?? key) }],
        key: key,
        displayName: displayName ?? key,
        alternateKeys,
        icon,
        platform: platform,
    }) as KeyCommandDisplayMapping;

export const KeybindingViewModelFromModel = (
    model: KeyCommandMapping,
    locale: string,
    alternateKeys: string[] = [],
    platform?: OSTypes
): KeyCommandDisplayMapping => {
    const defaultKeyMapping = getDefaultKeyMapping(locale, platform);
    const keyMapping = defaultKeyMapping.find((v) => v.key == model.key);
    return {
        commands: model.commands.map((c) => ({ command: c, commandType: GetCommandType(c) })),
        key: model.key,
        displayName: keyMapping?.displayName ?? model.key,
        alternateKeys,
        icon: getKeyIcon(model.key, locale),
        platform: keyMapping?.platform ?? 'generic', // key supported by the specified platform
    };
};

export const defaultKeyMappings: { [key in DisplayLocales]: KeyCommandDisplayMapping[] } = {
    en: [
        KeyBindingViewModel('mouse_button_1', 'Mouse Button 1 (Left Click)', 'mouseLeftClick', 'click', [
            'click',
            'left click',
        ]),
        KeyBindingViewModel('mouse_button_2', 'Mouse Button 2 (Right Click)', 'mouseRightClick', 'right click'),
        KeyBindingViewModel('mouse_button_3', 'Mouse Button 3 (Middle Click)', 'mouseMiddleClick', 'middle click'),

        KeyBindingViewModel('1', '1', 'key1', 'one', []),
        KeyBindingViewModel('2', '2', 'key2', 'two', []),
        KeyBindingViewModel('3', '3', 'key3', 'three', []),
        KeyBindingViewModel('4', '4', 'key4', 'four', []),
        KeyBindingViewModel('5', '5', 'key5', 'five', []),
        KeyBindingViewModel('6', '6', 'key6', 'six', []),
        KeyBindingViewModel('7', '7', 'key7', 'seven', []),
        KeyBindingViewModel('8', '8', 'key8', 'eight', []),
        KeyBindingViewModel('9', '9', 'key9', 'nine', []),
        KeyBindingViewModel('0', '0', 'key0', 'zero', []),

        KeyBindingViewModel('a', 'a', 'keyA'),
        KeyBindingViewModel('b', 'b', 'keyB'),
        KeyBindingViewModel('c', 'c', 'keyC'),
        KeyBindingViewModel('d', 'd', 'keyD'),
        KeyBindingViewModel('e', 'e', 'keyE'),
        KeyBindingViewModel('f', 'f', 'keyF'),
        KeyBindingViewModel('g', 'g', 'keyG'),
        KeyBindingViewModel('h', 'h', 'keyH'),
        KeyBindingViewModel('i', 'i', 'keyI'),
        KeyBindingViewModel('j', 'j', 'keyJ'),
        KeyBindingViewModel('k', 'k', 'keyK'),
        KeyBindingViewModel('l', 'l', 'keyL'),
        KeyBindingViewModel('m', 'm', 'keyM'),
        KeyBindingViewModel('n', 'n', 'keyN'),
        KeyBindingViewModel('o', 'o', 'keyO'),
        KeyBindingViewModel('p', 'p', 'keyP'),
        KeyBindingViewModel('q', 'q', 'keyQ', 'q'),
        KeyBindingViewModel('r', 'r', 'keyR'),
        KeyBindingViewModel('s', 's', 'keyS'),
        KeyBindingViewModel('t', 't', 'keyT'),
        KeyBindingViewModel('u', 'u', 'keyU'),
        KeyBindingViewModel('v', 'v', 'keyV'),
        KeyBindingViewModel('w', 'w', 'keyW'),
        KeyBindingViewModel('x', 'x', 'keyX'),
        KeyBindingViewModel('y', 'y', 'keyY'),
        KeyBindingViewModel('z', 'z', 'keyZ'),

        KeyBindingViewModel('_', '_', undefined, 'underscore'),
        KeyBindingViewModel('-', '-', undefined, 'dash', ['subtract']),
        KeyBindingViewModel(',', ',', undefined, 'comma', ['comma', 'Comma (,)', ',']),
        KeyBindingViewModel(';', ';', undefined, 'semi colon'),
        KeyBindingViewModel(':', ':', undefined, 'colin'), // our asr pulls 'colin' and not 'colon'
        KeyBindingViewModel('?', '?', undefined, 'question mark'),
        KeyBindingViewModel('.', '.', 'keyDot', 'period'),
        KeyBindingViewModel('"', '"', undefined, 'quote'),
        KeyBindingViewModel('(', '(', undefined, 'open parenthesis'),
        KeyBindingViewModel(')', ')', undefined, 'close parenthesis'),
        KeyBindingViewModel('[', '[', undefined, 'open brace'),
        KeyBindingViewModel(']', ']', undefined, 'close brace'),
        KeyBindingViewModel('{', '{', undefined, 'open bracket'),
        KeyBindingViewModel('}', '}', undefined, 'close bracket'),
        KeyBindingViewModel('!', '!', undefined, 'exclamation'),
        KeyBindingViewModel('@', '@', undefined, 'at sign'),
        KeyBindingViewModel('*', '*', 'keyAsterisk', 'asterisk', ['multiply']),
        KeyBindingViewModel('/', '/', 'keySolidus', 'forward slash', ['divide']),
        KeyBindingViewModel('\\', '\\', undefined, 'backslash'),
        KeyBindingViewModel('&', '&', undefined, 'ampersand'),
        KeyBindingViewModel('#', '#', 'keyHashtag', 'lb'), // our asr always pulls "lb" instead of "pound"....
        KeyBindingViewModel('%', '%', undefined, 'percent'),
        KeyBindingViewModel('`', '`', undefined, 'back tick'),
        KeyBindingViewModel('^', '^', undefined, 'carrot'), // our ASR always pulls "carrot" instead of "caret"
        KeyBindingViewModel('+', '+', undefined, 'plus', ['add']),
        KeyBindingViewModel('<', '<', 'keyLeftAngleParentheses', 'less than'),
        KeyBindingViewModel('=', '=', 'keyEqual', 'equals'),
        KeyBindingViewModel('>', '>', 'keyRightAngleParentheses', 'greater than'),
        KeyBindingViewModel('|', '|', undefined, 'pipe'),
        KeyBindingViewModel('~', '~', undefined, 'til de'),
        KeyBindingViewModel('$', '$', undefined, 'dollar sign'),
        KeyBindingViewModel("'", "'", undefined, 'single quote'),

        KeyBindingViewModel('backspace', 'Backspace', 'keyDelete', 'back space', ['back space', 'backspace']),
        KeyBindingViewModel('capslock', 'Caps', 'keyCapsLock', 'caps lock', ['caps lock', 'caps']),
        KeyBindingViewModel('numlock', 'Num Lock', undefined, 'num lock', ['num lock']),
        KeyBindingViewModel('scrolllock', 'Scroll Lock', undefined, 'scroll lock', ['scroll lock'], 'Windows'),
        KeyBindingViewModel('cmd', 'Command', 'keyCommand', 'command', ['leftcmd', 'command'], 'MacOS'),
        KeyBindingViewModel('rightcmd', 'Right Command', 'keyCommand', 'right command', [], 'MacOS'),
        KeyBindingViewModel('delete', 'Delete', 'keyDelete'),
        KeyBindingViewModel('down', 'Down Arrow', 'keyPageDownButton', 'move down', ['down arrow', 'downarrow']),
        KeyBindingViewModel('left', 'Left Arrow', 'keyHomeButton', 'move left', ['left arrow', 'leftarrow']),
        KeyBindingViewModel('right', 'Right Arrow', 'keyEndButton', 'move right', ['right arrow', 'rightarrow']),
        KeyBindingViewModel('up', 'Up Arrow', 'keyPageUpButton', 'move up', ['up arrow', 'uparrow']),

        KeyBindingViewModel('enter', 'Enter', 'keyEnterMac', 'enter', ['return']),
        KeyBindingViewModel('escape', 'Escape', 'keyEsc', 'escape', ['esc']),
        KeyBindingViewModel('leftalt', 'Left Alt', 'keyAlt', 'alt', ['alt'], 'Windows'),
        KeyBindingViewModel('leftcontrol', 'Left Ctrl', 'keyCtrl', 'control', ['ctrl', 'control']),
        KeyBindingViewModel('leftshift', 'Left Shift', 'shift', 'shift', ['shift']),
        KeyBindingViewModel('option', 'Option', 'keyOption', 'option', ['opt'], 'MacOS'),
        KeyBindingViewModel('rightcontrol', 'Right Ctrl', 'keyCtrl', 'right control'),
        KeyBindingViewModel('rightshift', 'Right Shift', 'shift', 'right shift'),
        KeyBindingViewModel('space', 'Space Bar', 'keySpace', 'space', ['space bar', 'spacebar']),
        KeyBindingViewModel('tab', 'Tab', 'keyTab'),
        KeyBindingViewModel('win', 'Windows', 'keyWindows', 'windows', ['windows'], 'Windows'),
        KeyBindingViewModel('fn', 'Function', 'keyFn', 'function', ['function', 'fn'], 'MacOS'),
        KeyBindingViewModel('menu', 'Menu', undefined, 'menu'),
        KeyBindingViewModel('printscreen', 'Print Screen', undefined, 'print screen', ['print screen'], 'Windows'),
        KeyBindingViewModel('insert', 'Insert', undefined, 'insert'),
        KeyBindingViewModel('home', 'Home', undefined, 'home', [], 'Windows'),
        KeyBindingViewModel('end', 'End', undefined, 'end', [], 'Windows'),
        KeyBindingViewModel('pageup', 'Page Up', undefined, 'page up', ['page up']),
        KeyBindingViewModel('pagedown', 'Page Down', undefined, 'page down', ['page down']),

        KeyBindingViewModel('numpad_0', 'Numpad 0', 'key0', 'num pad zero'),
        KeyBindingViewModel('numpad_1', 'Numpad 1', 'key1', 'num pad one'),
        KeyBindingViewModel('numpad_2', 'Numpad 2', 'key2', 'num pad two'),
        KeyBindingViewModel('numpad_3', 'Numpad 3', 'key3', 'num pad three'),
        KeyBindingViewModel('numpad_4', 'Numpad 4', 'key4', 'num pad four'),
        KeyBindingViewModel('numpad_5', 'Numpad 5', 'key5', 'num pad five'),
        KeyBindingViewModel('numpad_6', 'Numpad 6', 'key6', 'num pad six'),
        KeyBindingViewModel('numpad_7', 'Numpad 7', 'key7', 'num pad seven'),
        KeyBindingViewModel('numpad_8', 'Numpad 8', 'key8', 'num pad eight'),
        KeyBindingViewModel('numpad_9', 'Numpad 9', 'key9', 'num pad nine'),
        KeyBindingViewModel('numpad_decimal', 'Numpad Decimal', 'keyDot', 'num pad decimal'),

        KeyBindingViewModel('f1', 'F1', 'keyF1', 'function one'),
        KeyBindingViewModel('f2', 'F2', 'keyF2', 'function two'),
        KeyBindingViewModel('f3', 'F3', 'keyF3', 'function three'),
        KeyBindingViewModel('f4', 'F4', 'keyF4', 'function four'),
        KeyBindingViewModel('f5', 'F5', 'keyF5', 'function five'),
        KeyBindingViewModel('f6', 'F6', 'keyF6', 'function six'),
        KeyBindingViewModel('f7', 'F7', 'keyF7', 'function seven'),
        KeyBindingViewModel('f8', 'F8', 'keyF8', 'function eight'),
        KeyBindingViewModel('f9', 'F9', 'keyF9', 'function nine'),
        KeyBindingViewModel('f10', 'F10', 'keyF10', 'function ten'),
        KeyBindingViewModel('f11', 'F11', 'keyF11', 'function eleven'),
        KeyBindingViewModel('f12', 'F12', 'keyF12', 'function twelve'),

        KeyBindingViewModel('audio_mute', 'Mute Audio', undefined, 'mute audio', ['mute', 'mute audio', 'audio mute']),
        KeyBindingViewModel('audio_vol_down', 'Volume Down', undefined, 'volume down', ['volume down']),
        KeyBindingViewModel('audio_vol_up', 'Volume Up', undefined, 'volume up', ['volume up']),
        KeyBindingViewModel('audio_play', 'Play Audio', undefined, 'play audio', ['play']),
        KeyBindingViewModel('audio_stop', 'Stop Audio', undefined, 'stop audio', ['stop']),
        KeyBindingViewModel('audio_pause', 'Pause Audio', undefined, 'pause audio', ['pause']),
        KeyBindingViewModel('audio_prev', 'Previous Track', undefined, 'previous track', ['previous', 'audio_prev']),
        KeyBindingViewModel('audio_next', 'Next Track', undefined, 'next track', ['next']),
        KeyBindingViewModel('audio_rewind', 'Rewind Track', undefined, 'rewind track', ['rewind']),
        KeyBindingViewModel('audio_forward', 'Forward Track', undefined, 'forward track', ['forward']),
        KeyBindingViewModel('audio_repeat', 'Repeat Track', undefined, 'repeat track', ['repeat']),
        KeyBindingViewModel('audio_random', 'Shuffle Tracks', undefined, 'shuffle tracks', ['shuffle', 'random']),

        KeyBindingViewModel('gamepad_button_1', 'Controller Button 1 (A)', 'joystick', 'button one'),
        KeyBindingViewModel('gamepad_button_2', 'Controller Button 2 (B)', 'joystick', 'button two'),
        KeyBindingViewModel('gamepad_button_3', 'Controller Button 3 (X)', 'joystick', 'button three'),
        KeyBindingViewModel('gamepad_button_4', 'Controller Button 4 (Y)', 'joystick', 'button four'),
        KeyBindingViewModel('gamepad_button_5', 'Controller Button 5 (LB)', 'joystick', 'button five'),
        KeyBindingViewModel('gamepad_button_6', 'Controller Button 6 (RB)', 'joystick', 'button six'),
        KeyBindingViewModel('gamepad_button_7', 'Controller Button 7 (LT)', 'joystick', 'button seven'),
        KeyBindingViewModel('gamepad_button_8', 'Controller Button 8 (RT)', 'joystick', 'button eight'),
        KeyBindingViewModel('gamepad_button_9', 'Controller Button 9 (Options)', 'joystick', 'button nine'),
        KeyBindingViewModel('gamepad_button_10', 'Controller Button 10 (Pause)', 'joystick', 'button ten'),
        KeyBindingViewModel('gamepad_button_11', 'Controller Button 11 (L3)', 'joystick', 'button eleven'),
        KeyBindingViewModel('gamepad_button_12', 'Controller Button 12 (R3)', 'joystick', 'button twelve'),
        KeyBindingViewModel('gamepad_button_13', 'Controller D-Pad Up', 'joystick', 'direction up'),
        KeyBindingViewModel('gamepad_button_14', 'Controller D-Pad Down', 'joystick', 'direction down'),
        KeyBindingViewModel('gamepad_button_15', 'Controller D-Pad Left', 'joystick', 'direction left'),
        KeyBindingViewModel('gamepad_button_16', 'Controller D-Pad Right', 'joystick', 'direction right'),
    ],
    es: [
        KeyBindingViewModel('mouse_button_1', 'Botón 1 del Ratón (Clic Izquierdo)', 'mouseLeftClick', 'cliq', [
            'click',
            'left click',
        ]),
        KeyBindingViewModel('mouse_button_2', 'Botón 2 del Ratón (Clic Derecha)', 'mouseRightClick', 'cliq derecha'),
        KeyBindingViewModel('mouse_button_3', 'Botón 3 del Ratón (Clic Centro)', 'mouseMiddleClick', 'cliq centro'),

        KeyBindingViewModel('1', '1', 'key1', 'uno', []),
        KeyBindingViewModel('2', '2', 'key2', 'dos', []),
        KeyBindingViewModel('3', '3', 'key3', 'tres', []),
        KeyBindingViewModel('4', '4', 'key4', 'cuatro', []),
        KeyBindingViewModel('5', '5', 'key5', 'cinco', []),
        KeyBindingViewModel('6', '6', 'key6', 'seis', []),
        KeyBindingViewModel('7', '7', 'key7', 'siete', []),
        KeyBindingViewModel('8', '8', 'key8', 'ocho', []),
        KeyBindingViewModel('9', '9', 'key9', 'nueve', []),
        KeyBindingViewModel('0', '0', 'key0', 'cero', []),

        KeyBindingViewModel('a', 'a', 'keyA'),
        KeyBindingViewModel('b', 'b', 'keyB', 'be'),
        KeyBindingViewModel('c', 'c', 'keyC', 'se'),
        KeyBindingViewModel('d', 'd', 'keyD', 'de'),
        KeyBindingViewModel('e', 'e', 'keyE', 'eh'),
        KeyBindingViewModel('f', 'f', 'keyF', 'efe'),
        KeyBindingViewModel('g', 'g', 'keyG', 'je'),
        KeyBindingViewModel('h', 'h', 'keyH', 'hace'),
        KeyBindingViewModel('i', 'i', 'keyI'),
        KeyBindingViewModel('j', 'j', 'keyJ', 'jota'),
        KeyBindingViewModel('k', 'k', 'keyK', 'ca'),
        KeyBindingViewModel('l', 'l', 'keyL', 'ele'),
        KeyBindingViewModel('m', 'm', 'keyM', 'eh me'),
        KeyBindingViewModel('n', 'n', 'keyN', 'en e'),
        KeyBindingViewModel('o', 'o', 'keyO'),
        KeyBindingViewModel('p', 'p', 'keyP', 'pe'),
        KeyBindingViewModel('q', 'q', 'keyQ'),
        KeyBindingViewModel('r', 'r', 'keyR', 'eh rey'),
        KeyBindingViewModel('s', 's', 'keyS', 'ese'),
        KeyBindingViewModel('t', 't', 'keyT', 'te'),
        KeyBindingViewModel('u', 'u', 'keyU'),
        KeyBindingViewModel('v', 'v', 'keyV', 've'),
        KeyBindingViewModel('w', 'w', 'keyW', 'doble ve'),
        KeyBindingViewModel('x', 'x', 'keyX', 'equis'),
        KeyBindingViewModel('y', 'y', 'keyY'),
        KeyBindingViewModel('z', 'z', 'keyZ', 'zeta'),

        KeyBindingViewModel('_', '_', undefined, 'guion bajo'),
        KeyBindingViewModel('-', '-', undefined, 'guión', ['subtract']),
        KeyBindingViewModel(',', ',', undefined, 'coma', ['comma', 'Comma (,)', ',']),
        KeyBindingViewModel(';', ';', undefined, 'punto y coma'),
        KeyBindingViewModel(':', ':', undefined, 'colon'),
        KeyBindingViewModel('?', '?', undefined, 'interrogante'),
        KeyBindingViewModel('.', '.', 'keyDot', 'punto'),
        KeyBindingViewModel('"', '"', undefined, 'comilla doble'),
        KeyBindingViewModel('(', '(', undefined, 'paréntesis abierto'),
        KeyBindingViewModel(')', ')', undefined, 'paréntesis cerrado'),
        KeyBindingViewModel('[', '[', undefined, 'corchete abierto'),
        KeyBindingViewModel(']', ']', undefined, 'corchete cerrado'),
        KeyBindingViewModel('{', '{', undefined, 'llave abierto'),
        KeyBindingViewModel('}', '}', undefined, 'llave cerrado'),
        KeyBindingViewModel('!', '!', undefined, 'exclamación'),
        KeyBindingViewModel('@', '@', undefined, 'signo de a'),
        KeyBindingViewModel('*', '*', 'keyAsterisk', 'asterisco', ['multiply']),
        KeyBindingViewModel('/', '/', 'keySolidus', 'barra inclinada', ['divide']),
        KeyBindingViewModel('\\', '\\', undefined, 'barra invertida'),
        KeyBindingViewModel('&', '&', undefined, 'signo commercial'),
        KeyBindingViewModel('#', '#', 'keyHashtag', 'signo de número'),
        KeyBindingViewModel('%', '%', undefined, 'por ciento'),
        KeyBindingViewModel('`', '`', undefined, 'grave'),
        KeyBindingViewModel('^', '^', undefined, 'signo de intercalación'),
        KeyBindingViewModel('+', '+', 'keyPlus', 'más', ['add']),
        KeyBindingViewModel('<', '<', 'keyLeftAngleParentheses', 'menos que'),
        KeyBindingViewModel('=', '=', 'keyEqual', 'igual'),
        KeyBindingViewModel('>', '>', 'keyRightAngleParentheses', 'mas grande que'),
        KeyBindingViewModel('|', '|', undefined, 'linea vertical'),
        KeyBindingViewModel('~', '~', undefined, 'til de'),
        KeyBindingViewModel('$', '$', undefined, 'dólar'),
        KeyBindingViewModel("'", "'", undefined, 'comilla simple'),

        KeyBindingViewModel('backspace', 'Backspace', 'keyDelete', 'retroceso', ['back space', 'backspace']),
        KeyBindingViewModel('capslock', 'Caps', 'keyCapsLock', 'bloq mayús', ['caps lock', 'caps']),
        KeyBindingViewModel('numlock', 'Num Lock', undefined, 'bloqueo numerico', ['num lock']),
        KeyBindingViewModel(
            'scrolllock',
            'Scroll Lock',
            undefined,
            'bloqueo desplazamiento',
            ['scroll lock'],
            'Windows'
        ),
        KeyBindingViewModel('cmd', 'Command', 'keyCommand', 'command', ['leftcmd', 'command'], 'MacOS'),
        KeyBindingViewModel('rightcmd', 'Right Command', 'keyCommand', 'command derecha', [], 'MacOS'),
        KeyBindingViewModel('delete', 'Delete', 'keyDelete', 'borrar'),
        KeyBindingViewModel('down', 'Flecha Abajo', 'keyPageDownButton', 'abajo', ['down arrow', 'downarrow']),
        KeyBindingViewModel('left', 'Flecha Izquierdo', 'keyHomeButton', 'izquierda', ['left arrow', 'leftarrow']),
        KeyBindingViewModel('right', 'Flecha Derecha', 'keyEndButton', 'derecha', ['right arrow', 'rightarrow']),
        KeyBindingViewModel('up', 'Flecha Arriba', 'keyPageUpButton', 'arriba', ['up arrow', 'uparrow']),

        KeyBindingViewModel('enter', 'Enter', 'keyEnterMac', 'entrar', ['return']),
        KeyBindingViewModel('escape', 'Esc', 'keyEsc', 'escapar', ['esc']),
        KeyBindingViewModel('leftalt', 'Alt Izquierda', 'keyAlt', 'alt', ['alt'], 'Windows'),
        KeyBindingViewModel('leftcontrol', 'Ctrl Izquierda', 'keyCtrl', 'control', ['ctrl', 'control']),
        KeyBindingViewModel('leftshift', 'Shift Izquierda', 'shift', 'cambio', ['shift']),
        KeyBindingViewModel('option', 'Option', 'keyOption', 'opción', ['opt'], 'MacOS'),
        KeyBindingViewModel('rightcontrol', 'Ctrl Derecha', 'keyCtrl', 'control derecha'),
        KeyBindingViewModel('rightshift', 'Shift Derecha', 'shift', 'cambio derecha'),
        KeyBindingViewModel('space', 'Espacio', 'keySpace', 'espacio', ['space bar', 'spacebar']),
        KeyBindingViewModel('tab', 'Tab', 'keyTab', 'pestaña'),
        KeyBindingViewModel('win', 'Win', 'keyWindows', 'windows', ['windows'], 'Windows'),
        KeyBindingViewModel('fn', 'Function', 'keyFn', 'función', ['function', 'fn'], 'MacOS'),
        KeyBindingViewModel('menu', 'Menu', undefined, 'menu'),
        KeyBindingViewModel('printscreen', 'Print Screen', undefined, 'imprimir pantalla', ['print screen'], 'Windows'),
        KeyBindingViewModel('insert', 'Insert', undefined, 'insertar'),
        KeyBindingViewModel('home', 'Home', undefined, 'inicio', [], 'Windows'),
        KeyBindingViewModel('end', 'End', undefined, 'finalizar', [], 'Windows'),
        KeyBindingViewModel('pageup', 'Page Up', undefined, 'página arriba', ['page up']),
        KeyBindingViewModel('pagedown', 'Page Down', undefined, 'página abajo', ['page down']),

        KeyBindingViewModel('numpad_0', 'Numpad 0', 'key0', 'número cero'),
        KeyBindingViewModel('numpad_1', 'Numpad 1', 'key1', 'número uno'),
        KeyBindingViewModel('numpad_2', 'Numpad 2', 'key2', 'número dos'),
        KeyBindingViewModel('numpad_3', 'Numpad 3', 'key3', 'número tres'),
        KeyBindingViewModel('numpad_4', 'Numpad 4', 'key4', 'número cuatro'),
        KeyBindingViewModel('numpad_5', 'Numpad 5', 'key5', 'número cinco'),
        KeyBindingViewModel('numpad_6', 'Numpad 6', 'key6', 'número seis'),
        KeyBindingViewModel('numpad_7', 'Numpad 7', 'key7', 'número siete'),
        KeyBindingViewModel('numpad_8', 'Numpad 8', 'key8', 'número ocho'),
        KeyBindingViewModel('numpad_9', 'Numpad 9', 'key9', 'número nueve'),
        KeyBindingViewModel('numpad_decimal', 'Numpad Decimal', 'keyDot', 'número decimal'),

        KeyBindingViewModel('f1', 'F1', 'keyF1', 'función uno'),
        KeyBindingViewModel('f2', 'F2', 'keyF2', 'función dos'),
        KeyBindingViewModel('f3', 'F3', 'keyF3', 'función tres'),
        KeyBindingViewModel('f4', 'F4', 'keyF4', 'función cuatro'),
        KeyBindingViewModel('f5', 'F5', 'keyF5', 'función cinco'),
        KeyBindingViewModel('f6', 'F6', 'keyF6', 'función seis'),
        KeyBindingViewModel('f7', 'F7', 'keyF7', 'función siete'),
        KeyBindingViewModel('f8', 'F8', 'keyF8', 'función ocho'),
        KeyBindingViewModel('f9', 'F9', 'keyF9', 'función nueve'),
        KeyBindingViewModel('f10', 'F10', 'keyF10', 'función diez'),
        KeyBindingViewModel('f11', 'F11', 'keyF11', 'función once'),
        KeyBindingViewModel('f12', 'F12', 'keyF12', 'función doce'),

        KeyBindingViewModel('audio_mute', 'Mute Audio', undefined, 'silenciar', ['mute', 'mute audio', 'audio mute']),
        KeyBindingViewModel('audio_vol_down', 'Volume Down', undefined, 'baja volumen', ['volume down']),
        KeyBindingViewModel('audio_vol_up', 'Volume Up', undefined, 'sube volumen', ['volume up']),
        KeyBindingViewModel('audio_play', 'Play Audio', undefined, 'reproduce audio', ['play']),
        KeyBindingViewModel('audio_stop', 'Stop Audio', undefined, 'detener audio', ['stop']),
        KeyBindingViewModel('audio_pause', 'Pause Audio', undefined, 'pausa audio', ['pause']),
        KeyBindingViewModel('audio_prev', 'Previous Track', undefined, 'pista anterior', ['previous', 'audio_prev']),
        KeyBindingViewModel('audio_next', 'Next Track', undefined, 'siguiente pista', ['next']),
        KeyBindingViewModel('audio_rewind', 'Rewind Track', undefined, 'rebobinar pista', ['rewind']),
        KeyBindingViewModel('audio_forward', 'Forward Track', undefined, 'avance pista', ['forward']),
        KeyBindingViewModel('audio_repeat', 'Repeat Track', undefined, 'repita', ['repeat']),
        KeyBindingViewModel('audio_random', 'Shuffle Tracks', undefined, 'aleatoriamente pistas', [
            'shuffle',
            'random',
        ]),

        KeyBindingViewModel('gamepad_button_1', 'Botón 1 del Controlador (A)', 'joystick', 'botón uno'),
        KeyBindingViewModel('gamepad_button_2', 'Botón 2 del Controlador (B)', 'joystick', 'botón dos'),
        KeyBindingViewModel('gamepad_button_3', 'Botón 3 del Controlador (X)', 'joystick', 'botón tres'),
        KeyBindingViewModel('gamepad_button_4', 'Botón 4 del Controlador (Y)', 'joystick', 'botón cuatro'),
        KeyBindingViewModel('gamepad_button_5', 'Botón 5 del Controlador (LB)', 'joystick', 'botón cinco'),
        KeyBindingViewModel('gamepad_button_6', 'Botón 6 del Controlador (RB)', 'joystick', 'botón seis'),
        KeyBindingViewModel('gamepad_button_7', 'Botón 7 del Controlador (LT)', 'joystick', 'botón siete'),
        KeyBindingViewModel('gamepad_button_8', 'Botón 8 del Controlador (RT)', 'joystick', 'botón ocho'),
        KeyBindingViewModel('gamepad_button_9', 'Botón 8 del Controlador (Options)', 'joystick', 'botón nueve'),
        KeyBindingViewModel('gamepad_button_10', 'Botón 10 del Controlador (Pause)', 'joystick', 'botón diez'),
        KeyBindingViewModel('gamepad_button_11', 'Botón 11 del Controlador (L3)', 'joystick', 'botón once'),
        KeyBindingViewModel('gamepad_button_12', 'Botón 12 del Controlador(R3)', 'joystick', 'botón doce'),
        KeyBindingViewModel('gamepad_button_13', 'Controller D-Pad Arriba', 'joystick', 'dirección arriba'),
        KeyBindingViewModel('gamepad_button_14', 'Controller D-Pad Abajo', 'joystick', 'dirección abajo'),
        KeyBindingViewModel('gamepad_button_15', 'Controller D-Pad Izquierda', 'joystick', 'dirección izquierda'),
        KeyBindingViewModel('gamepad_button_16', 'Controller D-Pad Derecha', 'joystick', 'dirección derecha'),
    ],
    fr: [
        KeyBindingViewModel('mouse_button_1', 'Bouton 1 de la Souris (Clic Gauche)', 'mouseLeftClick', 'cliquez', [
            'click',
            'left click',
        ]),
        KeyBindingViewModel('mouse_button_2', 'Bouton 2 de la Souris (Clic Droit)', 'mouseRightClick', 'cli droit'),
        KeyBindingViewModel(
            'mouse_button_3',
            'Bouton 3 de la Souris (Clic Central)',
            'mouseMiddleClick',
            'clic central'
        ),

        KeyBindingViewModel('1', '1', 'key1', 'un', []),
        KeyBindingViewModel('2', '2', 'key2', 'deux', []),
        KeyBindingViewModel('3', '3', 'key3', 'trois', []),
        KeyBindingViewModel('4', '4', 'key4', 'quatre', []),
        KeyBindingViewModel('5', '5', 'key5', 'cinq', []),
        KeyBindingViewModel('6', '6', 'key6', 'six', []),
        KeyBindingViewModel('7', '7', 'key7', 'sept', []),
        KeyBindingViewModel('8', '8', 'key8', 'huit', []),
        KeyBindingViewModel('9', '9', 'key9', 'neuf', []),
        KeyBindingViewModel('0', '0', 'key0', 'zéro', []),

        KeyBindingViewModel('a', 'a', 'keyA', 'a'),
        KeyBindingViewModel('b', 'b', 'keyB'),
        KeyBindingViewModel('c', 'c', 'keyC'),
        KeyBindingViewModel('d', 'd', 'keyD'),
        KeyBindingViewModel('e', 'e', 'keyE'),
        KeyBindingViewModel('f', 'f', 'keyF', 'effe'),
        KeyBindingViewModel('g', 'g', 'keyG'),
        KeyBindingViewModel('h', 'h', 'keyH', 'hache'),
        KeyBindingViewModel('i', 'i', 'keyI'),
        KeyBindingViewModel('j', 'j', 'keyJ'),
        KeyBindingViewModel('k', 'k', 'keyK', 'ca'),
        KeyBindingViewModel('l', 'l', 'keyL'),
        KeyBindingViewModel('m', 'm', 'keyM', 'eh me'),
        KeyBindingViewModel('n', 'n', 'keyN', 'en e'),
        KeyBindingViewModel('o', 'o', 'keyO'),
        KeyBindingViewModel('p', 'p', 'keyP', 'pe'),
        KeyBindingViewModel('q', 'q', 'keyQ', 'qu'),
        KeyBindingViewModel('r', 'r', 'keyR', 'eh re'),
        KeyBindingViewModel('s', 's', 'keyS', 'ese'),
        KeyBindingViewModel('t', 't', 'keyT', 'te'),
        KeyBindingViewModel('u', 'u', 'keyU', 'u'),
        KeyBindingViewModel('v', 'v', 'keyV', 've'),
        KeyBindingViewModel('w', 'w', 'keyW', 'doble ve'),
        KeyBindingViewModel('x', 'x', 'keyX', 'ics'),
        KeyBindingViewModel('y', 'y', 'keyY', 'i grec'),
        KeyBindingViewModel('z', 'z', 'keyZ'),

        KeyBindingViewModel('_', '_', undefined, 'souligner'),
        KeyBindingViewModel('-', '-', undefined, 'tiret', ['subtract']),
        KeyBindingViewModel(',', ',', undefined, 'virgule', ['comma', 'Comma (,)', ',']),
        KeyBindingViewModel(';', ';', undefined, 'point virgule'),
        KeyBindingViewModel(':', ':', undefined, 'côlon'),
        KeyBindingViewModel('?', '?', undefined, "point d'interrogation"),
        KeyBindingViewModel('.', '.', 'keyDot', 'période'),
        KeyBindingViewModel('"', '"', undefined, 'citation'),
        KeyBindingViewModel('(', '(', undefined, 'parenthèse ouverte'),
        KeyBindingViewModel(')', ')', undefined, 'fermer la parenthèse'),
        KeyBindingViewModel('[', '[', undefined, 'accolade ouverte'),
        KeyBindingViewModel(']', ']', undefined, 'fermer ľaccolade'),
        KeyBindingViewModel('{', '{', undefined, 'crochet ouvert'),
        KeyBindingViewModel('}', '}', undefined, 'fermer le crochet'),
        KeyBindingViewModel('!', '!', undefined, 'exclamation'),
        KeyBindingViewModel('@', '@', undefined, 'arobase'),
        KeyBindingViewModel('*', '*', 'keyAsterisk', 'astérisque', ['multiply']),
        KeyBindingViewModel('/', '/', 'keySolidus', 'barre oblique', ['divide']),
        KeyBindingViewModel('\\', '\\', undefined, 'barre oblique inverse'),
        KeyBindingViewModel('&', '&', undefined, 'esperluette'),
        KeyBindingViewModel('#', '#', 'keyHashtag', 'hash tag'),
        KeyBindingViewModel('%', '%', undefined, 'pour cent'),
        KeyBindingViewModel('`', '`', undefined, 'tique arrièr'),
        KeyBindingViewModel('^', '^', undefined, 'carotte'),
        KeyBindingViewModel('+', '+', 'keyPlus', 'plus'),
        KeyBindingViewModel('<', '<', 'keyLeftAngleParentheses', 'moins que'),
        KeyBindingViewModel('=', '=', 'keyEqual', 'est égal à'),
        KeyBindingViewModel('>', '>', 'keyRightAngleParentheses', 'plus grand que'),
        KeyBindingViewModel('|', '|', undefined, 'tuyau'),
        KeyBindingViewModel('~', '~', undefined, 'til de'),
        KeyBindingViewModel('$', '$', undefined, 'signe dollar'),
        KeyBindingViewModel("'", "'", undefined, 'guillemet simple'),

        KeyBindingViewModel('backspace', 'Backspace', 'keyDelete', 'retour arrière', ['back space', 'backspace']),
        KeyBindingViewModel('capslock', 'Caps', 'keyCapsLock', 'verrouillage des majuscules', ['caps lock', 'caps']),
        KeyBindingViewModel('numlock', 'Num Lock', undefined, 'verrouillage numérique', ['num lock']),
        KeyBindingViewModel(
            'scrolllock',
            'Scroll Lock',
            undefined,
            'verrouillage du défilement',
            ['scroll lock'],
            'Windows'
        ),
        KeyBindingViewModel('cmd', 'Command', 'keyCommand', 'commande', ['leftcmd', 'command'], 'MacOS'),
        KeyBindingViewModel('rightcmd', 'Right Command', 'keyCommand', 'commande droite', [], 'MacOS'),
        KeyBindingViewModel('delete', 'Delete', 'keyDelete', 'supprimer'),
        KeyBindingViewModel('down', 'Down Arrow', 'keyPageDownButton', 'descendre', ['down arrow', 'downarrow']),
        KeyBindingViewModel('left', 'Left Arrow', 'keyHomeButton', 'gauche', ['left arrow', 'leftarrow']),
        KeyBindingViewModel('right', 'Right Arrow', 'keyEndButton', 'droite', ['right arrow', 'rightarrow']),
        KeyBindingViewModel('up', 'Up Arrow', 'keyPageUpButton', 'monter', ['up arrow', 'uparrow']),
        KeyBindingViewModel('enter', 'Enter', 'keyEnterMac', 'entrer', ['return']),
        KeyBindingViewModel('escape', 'Escape', 'keyEsc', 'séchapper', ['esc']),
        KeyBindingViewModel('leftalt', 'Left Alt', 'keyAlt', 'alt', ['alt'], 'Windows'),
        KeyBindingViewModel('leftcontrol', 'Left Ctrl', 'keyCtrl', 'contrôle', ['ctrl', 'control']),
        KeyBindingViewModel('leftshift', 'Left Shift', 'shift', 'changement', ['shift']),
        KeyBindingViewModel('option', 'Option', 'keyOption', 'option', ['opt'], 'MacOS'),
        KeyBindingViewModel('rightcontrol', 'Right Ctrl', 'keyCtrl', 'contrôle droit'),
        KeyBindingViewModel('rightshift', 'Right Shift', 'shift', 'changement droit'),
        KeyBindingViewModel('space', 'Space Bar', 'keySpace', 'espace', ['space bar', 'spacebar']),
        KeyBindingViewModel('tab', 'Tab', 'keyTab', 'languette'),
        KeyBindingViewModel('win', 'Windows', 'keyWindows', 'fenêtres', ['windows'], 'Windows'),
        KeyBindingViewModel('fn', 'Function', 'keyFn', 'fonction', ['function', 'fn'], 'MacOS'),
        KeyBindingViewModel('menu', 'Menu', undefined, 'menu'),
        KeyBindingViewModel(
            'printscreen',
            'Print Screen',
            undefined,
            "écran d'impression",
            ['print screen'],
            'Windows'
        ),
        KeyBindingViewModel('insert', 'Insert', undefined, 'insérer'),
        KeyBindingViewModel('home', 'Home', undefined, "d'accueil", [], 'Windows'),
        KeyBindingViewModel('end', 'End', undefined, 'fin', [], 'Windows'),
        KeyBindingViewModel('pageup', 'Page Up', undefined, 'page précédente', ['page up']),
        KeyBindingViewModel('pagedown', 'Page Down', undefined, 'page suivante', ['page down']),

        KeyBindingViewModel('numpad_0', 'Numpad 0', 'key0', 'pavé numérique zéro'),
        KeyBindingViewModel('numpad_1', 'Numpad 1', 'key1', 'pavé numérique un'),
        KeyBindingViewModel('numpad_2', 'Numpad 2', 'key2', 'pavé numérique deux'),
        KeyBindingViewModel('numpad_3', 'Numpad 3', 'key3', 'pavé numérique trois'),
        KeyBindingViewModel('numpad_4', 'Numpad 4', 'key4', 'pavé numérique quatre'),
        KeyBindingViewModel('numpad_5', 'Numpad 5', 'key5', 'pavé numérique cinq'),
        KeyBindingViewModel('numpad_6', 'Numpad 6', 'key6', 'pavé numérique six'),
        KeyBindingViewModel('numpad_7', 'Numpad 7', 'key7', 'pavé numérique sept'),
        KeyBindingViewModel('numpad_8', 'Numpad 8', 'key8', 'pavé numérique huit'),
        KeyBindingViewModel('numpad_9', 'Numpad 9', 'key9', 'pavé numérique neuf'),
        KeyBindingViewModel('numpad_decimal', 'Numpad Decimal', 'keyDot', 'pavé numérique décimal'),

        KeyBindingViewModel('f1', 'F1', 'keyF1', 'fonction un'),
        KeyBindingViewModel('f2', 'F2', 'keyF2', 'fonction deux'),
        KeyBindingViewModel('f3', 'F3', 'keyF3', 'fonction trois'),
        KeyBindingViewModel('f4', 'F4', 'keyF4', 'fonction quatre'),
        KeyBindingViewModel('f5', 'F5', 'keyF5', 'fonction cinq'),
        KeyBindingViewModel('f6', 'F6', 'keyF6', 'fonction six'),
        KeyBindingViewModel('f7', 'F7', 'keyF7', 'fonction sept'),
        KeyBindingViewModel('f8', 'F8', 'keyF8', 'fonction huit'),
        KeyBindingViewModel('f9', 'F9', 'keyF9', 'fonction neuf'),
        KeyBindingViewModel('f10', 'F10', 'keyF10', 'fonction dix'),
        KeyBindingViewModel('f11', 'F11', 'keyF11', 'fonction onze'),
        KeyBindingViewModel('f12', 'F12', 'keyF12', 'fonction douze'),

        KeyBindingViewModel('audio_mute', 'Mute Audio', undefined, 'couper son', ['mute', 'mute audio', 'audio mute']),
        KeyBindingViewModel('audio_vol_down', 'Volume Down', undefined, 'baisser volume', ['volume down']),
        KeyBindingViewModel('audio_vol_up', 'Volume Up', undefined, 'augmenter volume', ['volume up']),
        KeyBindingViewModel('audio_play', 'Play Audio', undefined, 'lire son', ['play']),
        KeyBindingViewModel('audio_stop', 'Stop Audio', undefined, 'arrêter son', ['stop']),
        KeyBindingViewModel('audio_pause', 'Pause Audio', undefined, 'mettre son pause', ['pause']),
        KeyBindingViewModel('audio_prev', 'Previous Track', undefined, 'piste précédente', ['previous', 'audio_prev']),
        KeyBindingViewModel('audio_next', 'Next Track', undefined, 'piste suivante', ['next']),
        KeyBindingViewModel('audio_rewind', 'Rewind Track', undefined, 'rembobiner piste', ['rewind']),
        KeyBindingViewModel('audio_forward', 'Forward Track', undefined, 'piste avant', ['forward']),
        KeyBindingViewModel('audio_repeat', 'Repeat Track', undefined, 'répéter piste', ['repeat']),
        KeyBindingViewModel('audio_random', 'Shuffle Tracks', undefined, 'mélanger pistes', ['shuffle', 'random']),

        KeyBindingViewModel('gamepad_button_1', 'Bouton du Contrôleur 1 (A)', 'joystick', 'bouton un'),
        KeyBindingViewModel('gamepad_button_2', 'Bouton du Contrôleur 2 (B)', 'joystick', 'bouton deux'),
        KeyBindingViewModel('gamepad_button_3', 'Bouton du Contrôleur 3 (X)', 'joystick', 'bouton trois'),
        KeyBindingViewModel('gamepad_button_4', 'Bouton du Contrôleur 4 (Y)', 'joystick', 'bouton quatre'),
        KeyBindingViewModel('gamepad_button_5', 'Bouton du Contrôleur 5 (LB)', 'joystick', 'bouton cinq'),
        KeyBindingViewModel('gamepad_button_6', 'Bouton du Contrôleur 6 (RB)', 'joystick', 'bouton six'),
        KeyBindingViewModel('gamepad_button_7', 'Bouton du Contrôleur 7 (LT)', 'joystick', 'bouton sept'),
        KeyBindingViewModel('gamepad_button_8', 'Bouton du Contrôleur 8 (RT)', 'joystick', 'bouton huit'),
        KeyBindingViewModel('gamepad_button_9', 'Bouton du Contrôleur 9 (Options)', 'joystick', 'bouton neuf'),
        KeyBindingViewModel('gamepad_button_10', 'Bouton du Contrôleur 10 (Pause)', 'joystick', 'bouton dix'),
        KeyBindingViewModel('gamepad_button_11', 'Bouton du Contrôleur 11 (L3)', 'joystick', 'bouton onze'),
        KeyBindingViewModel('gamepad_button_12', 'Bouton du Contrôleur 12 (R3)', 'joystick', 'bouton douze'),
        KeyBindingViewModel('gamepad_button_13', 'Contrôleur D-Pad En Haut', 'joystick', 'direction vers haut'),
        KeyBindingViewModel('gamepad_button_14', 'Contrôleur D-Pad Vers le Bas', 'joystick', 'direction vers bas'),
        KeyBindingViewModel('gamepad_button_15', 'Contrôleur D-Pad Gauche', 'joystick', 'direction gauche'),
        KeyBindingViewModel('gamepad_button_16', 'Contrôleur D-Pad Droit', 'joystick', 'direction à droite'),
    ],
    it: [
        KeyBindingViewModel('mouse_button_1', 'Pulsante 1 Del Mouse (Clic Sinistro)', 'mouseLeftClick', 'clic', [
            'click',
            'left click',
        ]),
        KeyBindingViewModel('mouse_button_2', 'Pulsante 2 Del Mouse (Clic Destro)', 'mouseRightClick', 'clic destro'),
        KeyBindingViewModel(
            'mouse_button_3',
            'Pulsante 3 Del Mouse (Clic Centrale)',
            'mouseMiddleClick',
            'clic centrale'
        ),

        KeyBindingViewModel('1', '1', 'key1', 'uno', []),
        KeyBindingViewModel('2', '2', 'key2', 'due', []),
        KeyBindingViewModel('3', '3', 'key3', 'tre', []),
        KeyBindingViewModel('4', '4', 'key4', 'quattro', []),
        KeyBindingViewModel('5', '5', 'key5', 'cinque', []),
        KeyBindingViewModel('6', '6', 'key6', 'sei', []),
        KeyBindingViewModel('7', '7', 'key7', 'sette', []),
        KeyBindingViewModel('8', '8', 'key8', 'otto', []),
        KeyBindingViewModel('9', '9', 'key9', 'nove', []),
        KeyBindingViewModel('0', '0', 'key0', 'zero', []),

        KeyBindingViewModel('a', 'A', 'keyA'),
        KeyBindingViewModel('b', 'B', 'keyB', 'bi'),
        KeyBindingViewModel('c', 'C', 'keyC', 'si'),
        KeyBindingViewModel('d', 'D', 'keyD', 'di'),
        KeyBindingViewModel('e', 'E', 'keyE'),
        KeyBindingViewModel('f', 'F', 'keyF', 'efe'),
        KeyBindingViewModel('g', 'G', 'keyG', 'ji'),
        KeyBindingViewModel('h', 'H', 'keyH', 'acca'),
        KeyBindingViewModel('i', 'I', 'keyI'),
        KeyBindingViewModel('j', 'J', 'keyJ'),
        KeyBindingViewModel('k', 'K', 'keyK'),
        KeyBindingViewModel('l', 'L', 'keyL', 'ele'),
        KeyBindingViewModel('m', 'M', 'keyM', 'eh me'),
        KeyBindingViewModel('n', 'N', 'keyN', 'en e'),
        KeyBindingViewModel('o', 'O', 'keyO'),
        KeyBindingViewModel('p', 'P', 'keyP', 'pi'),
        KeyBindingViewModel('q', 'Q', 'keyQ', 'cu'),
        KeyBindingViewModel('r', 'R', 'keyR', 'eh re'),
        KeyBindingViewModel('s', 'S', 'keyS', 'ese'),
        KeyBindingViewModel('t', 'T', 'keyT', 'ti'),
        KeyBindingViewModel('u', 'U', 'keyU'),
        KeyBindingViewModel('v', 'V', 'keyV', 've'),
        KeyBindingViewModel('w', 'W', 'keyW', 'doppia ve'),
        KeyBindingViewModel('x', 'X', 'keyX', 'ics'),
        KeyBindingViewModel('y', 'Y', 'keyY', 'ipsilon'),
        KeyBindingViewModel('z', 'Z', 'keyZ', 'zeta'),

        KeyBindingViewModel('_', '_', undefined, 'trattino basso'),
        KeyBindingViewModel('-', '-', undefined, 'trattino', ['subtract']),
        KeyBindingViewModel(',', ',', undefined, 'virgola', ['comma', 'Comma (,)', ',']),
        KeyBindingViewModel(';', ';', undefined, 'punto e virgola'),
        KeyBindingViewModel(':', ':', undefined, 'due punti'),
        KeyBindingViewModel('?', '?', undefined, 'punto interrogativo'),
        KeyBindingViewModel('.', '.', 'keyDot', 'punto'),
        KeyBindingViewModel('"', '"', undefined, 'virgolette'),
        KeyBindingViewModel('(', '(', undefined, 'parentesi aperta'),
        KeyBindingViewModel(')', ')', undefined, 'parentesi chiusa'),
        KeyBindingViewModel('[', '[', undefined, 'parentesi quadra aperta'),
        KeyBindingViewModel(']', ']', undefined, 'parentesi quadra chiusa'),
        KeyBindingViewModel('{', '{', undefined, 'parentesi graffa aperta'),
        KeyBindingViewModel('}', '}', undefined, 'parentesi graffa chiusa'),
        KeyBindingViewModel('!', '!', undefined, 'punto esclamativo'),
        KeyBindingViewModel('@', '@', undefined, 'chiocciola'),
        KeyBindingViewModel('*', '*', 'keyAsterisk', 'asterisco', ['multiply']),
        KeyBindingViewModel('/', '/', 'keySolidus', 'barra', ['divide']),
        KeyBindingViewModel('\\', '\\', undefined, 'barra inversa'),
        KeyBindingViewModel('&', '&', undefined, 'e'),
        KeyBindingViewModel('#', '#', 'keyHashtag', 'cancelletto'),
        KeyBindingViewModel('%', '%', undefined, 'percentuale'),
        KeyBindingViewModel('`', '`', undefined, 'accento grave'),
        KeyBindingViewModel('^', '^', undefined, 'accento circonflesso'),
        KeyBindingViewModel('+', '+', undefined, 'più', ['add']),
        KeyBindingViewModel('<', '<', 'keyLeftAngleParentheses', 'minore di'),
        KeyBindingViewModel('=', '=', 'keyEqual', 'uguale'),
        KeyBindingViewModel('>', '>', 'keyRightAngleParentheses', 'maggiore di'),
        KeyBindingViewModel('|', '|', undefined, 'barra verticale'),
        KeyBindingViewModel('~', '~', undefined, 'til de'),
        KeyBindingViewModel('$', '$', undefined, 'dollaro'),
        KeyBindingViewModel("'", "'", undefined, 'apostrofo'),

        KeyBindingViewModel('backspace', 'Indietro', 'keyDelete', 'indietro', ['back space', 'backspace']),
        KeyBindingViewModel('capslock', 'Blocco Maiuscole', 'keyCapsLock', 'blocco maiuscole', ['caps lock', 'caps']),
        KeyBindingViewModel('numlock', 'Blocco Numerico', undefined, 'blocco numerico', ['num lock']),
        KeyBindingViewModel(
            'scrolllock',
            'Blocco Scorrimento',
            undefined,
            'blocco scorrimento',
            ['scroll lock'],
            'Windows'
        ),
        KeyBindingViewModel('cmd', 'Comando', 'keyCommand', 'comando', ['leftcmd', 'command'], 'MacOS'),
        KeyBindingViewModel('rightcmd', 'Comando Destro', 'keyCommand', 'comando destro', [], 'MacOS'),
        KeyBindingViewModel('delete', 'Cancella', 'keyDelete', 'eliminare'),
        KeyBindingViewModel('down', 'Freccia Giù', 'keyPageDownButton', 'giu', ['down arrow', 'downarrow']),
        KeyBindingViewModel('left', 'Freccia Sinistra', 'keyHomeButton', 'sinistra', ['left arrow', 'leftarrow']),
        KeyBindingViewModel('right', 'Freccia Destra', 'keyEndButton', 'destra', ['right arrow', 'rightarrow']),
        KeyBindingViewModel('up', 'Freccia Su', 'keyPageUpButton', 'su', ['up arrow', 'uparrow']),

        KeyBindingViewModel('enter', 'Entrare', 'keyEnterMac', 'entrare', ['return']),
        KeyBindingViewModel('escape', 'Fuga', 'keyEsc', 'fuga', ['esc']),
        KeyBindingViewModel('leftalt', 'Alt Sinistra', 'keyAlt', 'altra', ['alt'], 'Windows'),
        KeyBindingViewModel('leftcontrol', 'Ctrl Sinistra', 'keyCtrl', 'controllo', ['ctrl', 'control']),
        KeyBindingViewModel('leftshift', 'Shift Sinistra', 'shift', 'spostare', ['shift']),
        KeyBindingViewModel('option', 'Opzione', 'keyOption', 'opzione', ['opt'], 'MacOS'),
        KeyBindingViewModel('rightcontrol', 'Ctrl Destra', 'keyCtrl', 'control destra'),
        KeyBindingViewModel('rightshift', 'Shift Destra', 'shift', 'spostamento destra'),
        KeyBindingViewModel('space', 'Barra Spaziatrice', 'keySpace', 'spazio', ['space bar', 'spacebar']),
        KeyBindingViewModel('tab', 'Tab', 'keyTab', 'scheda'),
        KeyBindingViewModel('win', 'Windows', 'keyWindows', 'finestre', ['windows'], 'Windows'),
        KeyBindingViewModel('fn', 'Funzione', 'keyFn', 'funzione', ['function', 'fn'], 'MacOS'),
        KeyBindingViewModel('menu', 'Menu', undefined, 'menu'),
        KeyBindingViewModel('printscreen', 'Print Screen', undefined, 'stampa schermo', ['print screen'], 'Windows'),
        KeyBindingViewModel('insert', 'Insert', undefined, 'inserisci'),
        KeyBindingViewModel('home', 'Home', undefined, 'iniziale', [], 'Windows'),
        KeyBindingViewModel('end', 'End', undefined, 'fine', [], 'Windows'),
        KeyBindingViewModel('pageup', 'Pagina Su', undefined, 'pagina su', ['page up']),
        KeyBindingViewModel('pagedown', 'Pagina Giù', undefined, 'pagina giù', ['page down']),

        KeyBindingViewModel('numpad_0', 'Numpad 0', 'key0', 'tastierino numerico zero'),
        KeyBindingViewModel('numpad_1', 'Numpad 1', 'key1', 'tastierino numerico uno'),
        KeyBindingViewModel('numpad_2', 'Numpad 2', 'key2', 'tastierino numerico due'),
        KeyBindingViewModel('numpad_3', 'Numpad 3', 'key3', 'tastierino numerico tre'),
        KeyBindingViewModel('numpad_4', 'Numpad 4', 'key4', 'tastierino numerico quattro'),
        KeyBindingViewModel('numpad_5', 'Numpad 5', 'key5', 'tastierino numerico cinque'),
        KeyBindingViewModel('numpad_6', 'Numpad 6', 'key6', 'tastierino numerico sei'),
        KeyBindingViewModel('numpad_7', 'Numpad 7', 'key7', 'tastierino numerico sette'),
        KeyBindingViewModel('numpad_8', 'Numpad 8', 'key8', 'tastierino numerico otto'),
        KeyBindingViewModel('numpad_9', 'Numpad 9', 'key9', 'tastierino numerico nove'),
        KeyBindingViewModel('numpad_decimal', 'Numpad Decimale', 'keyDot', 'tastierino numerico decimale'),

        KeyBindingViewModel('f1', 'F1', 'keyF1', 'funzione uno'),
        KeyBindingViewModel('f2', 'F2', 'keyF2', 'funzione due'),
        KeyBindingViewModel('f3', 'F3', 'keyF3', 'funzione tre'),
        KeyBindingViewModel('f4', 'F4', 'keyF4', 'funzione quattro'),
        KeyBindingViewModel('f5', 'F5', 'keyF5', 'funzione cinque'),
        KeyBindingViewModel('f6', 'F6', 'keyF6', 'funzione sei'),
        KeyBindingViewModel('f7', 'F7', 'keyF7', 'funzione sette'),
        KeyBindingViewModel('f8', 'F8', 'keyF8', 'funzione otto'),
        KeyBindingViewModel('f9', 'F9', 'keyF9', 'funzione nove'),
        KeyBindingViewModel('f10', 'F10', 'keyF10', 'funzione dieci'),
        KeyBindingViewModel('f11', 'F11', 'keyF11', 'funzione undici'),
        KeyBindingViewModel('f12', 'F12', 'keyF12', 'funzione dodici'),

        KeyBindingViewModel('audio_mute', 'Disattiva Audio', undefined, 'disattiva audio', [
            'mute',
            'mute audio',
            'audio mute',
        ]),
        KeyBindingViewModel('audio_vol_down', 'Volume Giù', undefined, 'volume giù', ['volume down']),
        KeyBindingViewModel('audio_vol_up', 'Volume Su', undefined, 'volume su', ['volume up']),
        KeyBindingViewModel('audio_play', 'Riproduci Audio', undefined, 'riproduci audio', ['play']),
        KeyBindingViewModel('audio_stop', 'Ferma Audio', undefined, 'ferma audio', ['stop']),
        KeyBindingViewModel('audio_pause', 'Pausa Audio', undefined, 'pausa audio', ['pause']),
        KeyBindingViewModel('audio_prev', 'Traccia Precedente', undefined, 'traccia precedente', [
            'previous',
            'audio_prev',
        ]),
        KeyBindingViewModel('audio_next', 'Traccia Successiva', undefined, 'traccia successiva', ['next']),
        KeyBindingViewModel('audio_rewind', 'Riavvolgi Traccia', undefined, 'riavvolgi traccia', ['rewind']),
        KeyBindingViewModel('audio_forward', 'Avanza Traccia', undefined, 'avanza traccia', ['forward']),
        KeyBindingViewModel('audio_repeat', 'Ripeti Traccia', undefined, 'ripeti traccia', ['repeat']),
        KeyBindingViewModel('audio_random', 'Riproduzione Casuale', undefined, 'riproduzione casuale', [
            'shuffle',
            'random',
        ]),

        KeyBindingViewModel('gamepad_button_1', 'Pulsante 1 Del Controller (A)', 'joystick', 'pulsante uno'),
        KeyBindingViewModel('gamepad_button_2', 'Pulsante 2 Del Controller (B)', 'joystick', 'pulsante due'),
        KeyBindingViewModel('gamepad_button_3', 'Pulsante 3 Del Controller (X)', 'joystick', 'pulsante tre'),
        KeyBindingViewModel('gamepad_button_4', 'Pulsante 4 Del Controller (Y)', 'joystick', 'pulsante quattro'),
        KeyBindingViewModel('gamepad_button_5', 'Pulsante 5 Del Controller (LB)', 'joystick', 'pulsante cinque'),
        KeyBindingViewModel('gamepad_button_6', 'Pulsante 6 Del Controller (RB)', 'joystick', 'pulsante sei'),
        KeyBindingViewModel('gamepad_button_7', 'Pulsante 7 Del Controller (LT)', 'joystick', 'pulsante sette'),
        KeyBindingViewModel('gamepad_button_8', 'Pulsante 8 Del Controller (RT)', 'joystick', 'pulsante otto'),
        KeyBindingViewModel('gamepad_button_9', 'Pulsante 9 Del Controller (Opzioni)', 'joystick', 'pulsante nove'),
        KeyBindingViewModel('gamepad_button_10', 'Pulsante 10 Del Controller (Pausa)', 'joystick', 'pulsante dieci'),
        KeyBindingViewModel('gamepad_button_11', 'Pulsante 11 Del Controller (L3)', 'joystick', 'pulsante undici'),
        KeyBindingViewModel('gamepad_button_12', 'Pulsante 12 Del Controller (R3)', 'joystick', 'pulsante dodici'),
        KeyBindingViewModel('gamepad_button_13', 'Controller D-Pad Su', 'joystick', 'direzione su'),
        KeyBindingViewModel('gamepad_button_14', 'Controller D-Pad Giù', 'joystick', 'direzione giù'),
        KeyBindingViewModel('gamepad_button_15', 'Controller D-Pad Sinistra', 'joystick', 'direzione sinistra'),
        KeyBindingViewModel('gamepad_button_16', 'Controller D-Pad Destra', 'joystick', 'direzione destra'),
    ],
    de: [
        KeyBindingViewModel('mouse_button_1', 'Maustaste 1 (linksklick)', 'mouseLeftClick', 'klicken', [
            'click',
            'left click',
        ]),
        KeyBindingViewModel('mouse_button_2', 'Maustaste 2 (rechtsklick)', 'mouseRightClick', 'rechtsklick', []),
        KeyBindingViewModel(
            'mouse_button_3',
            'Maustaste 3 (mittlerer Klick)',
            'mouseMiddleClick',
            'mittlerer klick',
            []
        ),

        KeyBindingViewModel('1', '1', 'key1', 'eins', []),
        KeyBindingViewModel('2', '2', 'key2', 'zwei', []),
        KeyBindingViewModel('3', '3', 'key3', 'drei', []),
        KeyBindingViewModel('4', '4', 'key4', 'vier', []),
        KeyBindingViewModel('5', '5', 'key5', 'fünf', []),
        KeyBindingViewModel('6', '6', 'key6', 'sechs', []),
        KeyBindingViewModel('7', '7', 'key7', 'sieben', []),
        KeyBindingViewModel('8', '8', 'key8', 'acht', []),
        KeyBindingViewModel('9', '9', 'key9', 'neun', []),
        KeyBindingViewModel('0', '0', 'key0', 'null', []),

        KeyBindingViewModel('a', 'A', 'keyA', 'a', []),
        KeyBindingViewModel('b', 'B', 'keyB', 'b', []),
        KeyBindingViewModel('c', 'C', 'keyC', 'c', []),
        KeyBindingViewModel('d', 'D', 'keyD', 'd', []),
        KeyBindingViewModel('e', 'Und', 'keyE', 'e', []),
        KeyBindingViewModel('f', 'F', 'keyF', 'ef', []),
        KeyBindingViewModel('g', 'G', 'keyG', 'ge', []),
        KeyBindingViewModel('h', 'H', 'keyH', 'ha', []),
        KeyBindingViewModel('i', 'Ich', 'keyI', 'ee', []),
        KeyBindingViewModel('j', 'J', 'keyJ', 'yot', []),
        KeyBindingViewModel('k', 'K', 'keyK', 'ca', []),
        KeyBindingViewModel('l', 'M', 'keyL', 'l', []),
        KeyBindingViewModel('m', 'M', 'keyM', 'em', []),
        KeyBindingViewModel('n', 'N', 'keyN', 'n', []),
        KeyBindingViewModel('o', 'Die', 'keyO', 'oh', []),
        KeyBindingViewModel('p', 'P', 'keyP', 'p', []),
        KeyBindingViewModel('q', 'Q', 'keyQ', 'koo', []),
        KeyBindingViewModel('r', 'R', 'keyR', 'r', []),
        KeyBindingViewModel('s', 'S', 'keyS', 's', []),
        KeyBindingViewModel('t', 'T', 'keyT', 't', []),
        KeyBindingViewModel('u', 'In', 'keyU', 'u', []),
        KeyBindingViewModel('v', 'V', 'keyV', 'vow', []),
        KeyBindingViewModel('w', 'In', 'keyW', 'vee', []),
        KeyBindingViewModel('x', 'X', 'keyX', 'iks', []),
        KeyBindingViewModel('y', 'Und', 'keyY', 'oopsilohn', []),
        KeyBindingViewModel('z', 'Mit', 'keyZ', 'zet', []),

        KeyBindingViewModel('_', '_', 'keySpace', 'unterstreichen', []),
        KeyBindingViewModel('-', '-', 'keySpace', 'bindestrich', ['subtract']),
        KeyBindingViewModel(',', ',', 'keySpace', 'komma', ['comma', 'Comma (,)', ',']),
        KeyBindingViewModel(';', ';', 'keySpace', 'semi kolon', []),
        KeyBindingViewModel(':', ':', 'keySpace', 'doppelpunkt', []),
        KeyBindingViewModel('?', '?', 'keySpace', 'fragezeichen', []),
        KeyBindingViewModel('.', '.', 'keyDot', 'zeitraum', []),
        KeyBindingViewModel('"', '"', 'keySpace', 'zitat', []),
        KeyBindingViewModel('(', '(', 'keySpace', 'öffnende klammer', []),
        KeyBindingViewModel(')', ')', 'keySpace', 'schließende klammer', []),
        KeyBindingViewModel('[', '[', 'keySpace', 'öffnende eckige klammer', []),
        KeyBindingViewModel(']', ']', 'keySpace', 'schließende eckige klammer', []),
        KeyBindingViewModel('{', '{', 'keySpace', 'offene stützstrebe', []),
        KeyBindingViewModel('}', '}', 'keySpace', 'schließen stützbandage', []),
        KeyBindingViewModel('!', '!', 'keySpace', 'ausruf', []),
        KeyBindingViewModel('@', '@', 'keySpace', 'at zeichen', []),
        KeyBindingViewModel('*', '*', 'keyAsterisk', 'sternchen', ['multiply']),
        KeyBindingViewModel('/', '/', 'keySolidus', 'schrägstrich', ['divide']),
        KeyBindingViewModel('\\', '\\', 'keySpace', 'umgekehrter schrägstrich', []),
        KeyBindingViewModel('&', 'Und', 'keySpace', 'kaufmännisches und zeichen', []),
        KeyBindingViewModel('#', '#', 'keyHashtag', 'pfund', []),
        KeyBindingViewModel('%', '%', 'keySpace', 'prozent', []),
        KeyBindingViewModel('`', '`', 'keySpace', 'zurück häkchen', []),
        KeyBindingViewModel('^', '^', 'keySpace', 'karotte', []),
        KeyBindingViewModel('+', '+', 'keySpace', 'plus', ['add']),
        KeyBindingViewModel('<', '<', 'keyLeftAngleParentheses', 'weniger als', []),
        KeyBindingViewModel('=', '=', 'keyEqual', 'ist gleich', []),
        KeyBindingViewModel('>', '>', 'keyRightAngleParentheses', 'größer als', []),
        KeyBindingViewModel('|', '|', 'keySpace', 'rohr', []),
        KeyBindingViewModel('~', '~', 'keySpace', 'zu ihnen', []),
        KeyBindingViewModel('$', '$', 'keySpace', 'dollarzeichen', []),
        KeyBindingViewModel("'", "'", 'keySpace', 'einfaches anführungszeichen', []),

        KeyBindingViewModel('backspace', 'Rücktaste', 'keyDelete', 'rücktaste', ['back space', 'backspace']),
        KeyBindingViewModel('capslock', 'Kappen', 'keyCapsLock', 'feststelltaste', ['caps lock', 'caps']),
        KeyBindingViewModel('numlock', 'Num Lock', 'keySpace', 'zahlenschloss', ['num lock']),
        KeyBindingViewModel('scrolllock', 'Rollen-taste', 'keySpace', 'rollen taste', ['scroll lock'], 'Windows'),
        KeyBindingViewModel('cmd', 'Befehl', 'keyCommand', 'befehl', ['leftcmd', 'command'], 'MacOS'),
        KeyBindingViewModel('rightcmd', 'Richtiger Befehl', 'keyCommand', 'richtiger befehl', [], 'MacOS'),
        KeyBindingViewModel('delete', 'Löschen', 'keyDelete', 'löschen', []),
        KeyBindingViewModel('down', 'Pfeil Nach Unten', 'keyPageDownButton', 'nach unten', ['down arrow', 'downarrow']),
        KeyBindingViewModel('left', 'Pfeil Nach Links', 'keyHomeButton', 'nach links gehen', [
            'left arrow',
            'leftarrow',
        ]),
        KeyBindingViewModel('right', 'Pfeil Nach Rechts', 'keyEndButton', 'nach rechts gehen', [
            'right arrow',
            'rightarrow',
        ]),
        KeyBindingViewModel('up', 'Pfeil Nach Oben', 'keyPageUpButton', 'nach oben', ['up arrow', 'uparrow']),
        KeyBindingViewModel('enter', 'Eingeben', 'keyEnterMac', 'eingeben', []),
        KeyBindingViewModel('escape', 'Flucht', 'keyEsc', 'flucht', ['esc']),
        KeyBindingViewModel('leftalt', 'Linke Alt-taste', 'keyAlt', 'alle', ['alt'], 'Windows'),
        KeyBindingViewModel('leftcontrol', 'Linke Strg-taste', 'keyCtrl', 'kontrolle', ['ctrl', 'control']),
        KeyBindingViewModel('leftshift', 'Linksverschiebung', 'shift', 'schicht', ['shift']),
        KeyBindingViewModel('option', 'Option', 'keyOption', 'option', ['opt'], 'MacOS'),
        KeyBindingViewModel('rightcontrol', 'Rechte Strg-taste', 'keyCtrl', 'rechte kontrolle', []),
        KeyBindingViewModel('rightshift', 'Rechtsverschiebung', 'shift', 'rechtsverschiebung', []),
        KeyBindingViewModel('space', 'Leertaste', 'keySpace', 'raum', ['space bar', 'spacebar']),
        KeyBindingViewModel('tab', 'Tab', 'keyTab', 'tab', []),
        KeyBindingViewModel('win', 'Windows', 'keyWindows', 'fenster', ['windows'], 'Windows'),
        KeyBindingViewModel('fn', 'Funktion', 'keyFn', 'funktion', ['function', 'fn'], 'MacOS'),
        KeyBindingViewModel('menu', 'Speisekarte', 'keySpace', 'speisekarte', []),
        KeyBindingViewModel(
            'printscreen',
            'Bildschirm Drucken',
            'keySpace',
            'bildschirm drucken',
            ['print screen'],
            'Windows'
        ),
        KeyBindingViewModel('insert', 'Einfügen', 'keySpace', 'einfügen', []),
        KeyBindingViewModel('home', 'Heim', 'keySpace', 'heim', [], 'Windows'),
        KeyBindingViewModel('end', 'Ende', 'keySpace', 'ende', [], 'Windows'),
        KeyBindingViewModel('pageup', 'Bild Nach Oben', 'keySpace', 'bild nach oben', ['page up']),
        KeyBindingViewModel('pagedown', 'Bild Ab', 'keySpace', 'bild ab', ['page down']),

        KeyBindingViewModel('numpad_0', 'Numpad 0', 'key0', 'nummernblock null', []),
        KeyBindingViewModel('numpad_1', 'Numpad 1', 'key1', 'nummernblock eins', []),
        KeyBindingViewModel('numpad_2', 'Numpad 2', 'key2', 'nummernblock zwei', []),
        KeyBindingViewModel('numpad_3', 'Numpad 3', 'key3', 'nummernblock drei', []),
        KeyBindingViewModel('numpad_4', 'Numpad 4', 'key4', 'nummernblock vier', []),
        KeyBindingViewModel('numpad_5', 'Numpad 5', 'key5', 'nummernblock fünf', []),
        KeyBindingViewModel('numpad_6', 'Numpad 6', 'key6', 'nummernblock sechs', []),
        KeyBindingViewModel('numpad_7', 'Numpad 7', 'key7', 'nummernblock sieben', []),
        KeyBindingViewModel('numpad_8', 'Numpad 8', 'key8', 'nummernblock acht', []),
        KeyBindingViewModel('numpad_9', 'Numpad 9', 'key9', 'nummernblock neun', []),
        KeyBindingViewModel('numpad_decimal', 'Numpad Dezimal', 'keyDot', 'nummernblock dezimal', []),

        KeyBindingViewModel('f1', 'F1', 'keyF1', 'funktion eins', []),
        KeyBindingViewModel('f2', 'F2', 'keyF2', 'funktion zwei', []),
        KeyBindingViewModel('f3', 'F3', 'keyF3', 'funktion drei', []),
        KeyBindingViewModel('f4', 'F4', 'keyF4', 'funktion vier', []),
        KeyBindingViewModel('f5', 'F5', 'keyF5', 'funktion fünf', []),
        KeyBindingViewModel('f6', 'F6', 'keyF6', 'funktion sechs', []),
        KeyBindingViewModel('f7', 'F7', 'keyF7', 'funktion sieben', []),
        KeyBindingViewModel('f8', 'F8', 'keyF8', 'funktion acht', []),
        KeyBindingViewModel('f9', 'F9', 'keyF9', 'funktion neun', []),
        KeyBindingViewModel('f10', 'F10', 'keyF10', 'funktion zehn', []),
        KeyBindingViewModel('f11', 'F11', 'keyF11', 'funktion elf', []),
        KeyBindingViewModel('f12', 'F12', 'keyF12', 'funktion zwölf', []),

        KeyBindingViewModel('audio_mute', 'Audio Stumm Schalten', 'keySpace', 'audio stumm schalten', [
            'mute',
            'mute audio',
            'audio mute',
        ]),
        KeyBindingViewModel('audio_vol_down', 'Lautstärke Verringern', 'keySpace', 'lautstärke verringern', [
            'volume down',
        ]),
        KeyBindingViewModel('audio_vol_up', 'Lautstärke Erhöhen', 'keySpace', 'lautstärke erhöhen', ['volume up']),
        KeyBindingViewModel('audio_play', 'Audio Abspielen', 'keySpace', 'audio abspielen', ['play']),
        KeyBindingViewModel('audio_stop', 'Audio Stoppen', 'keySpace', 'audio stoppen', ['stop']),
        KeyBindingViewModel('audio_pause', 'Pause Audio', 'keySpace', 'audio anhalten', ['pause']),
        KeyBindingViewModel('audio_prev', 'Vorheriger Titel', 'keySpace', 'vorheriger titel', [
            'previous',
            'audio_prev',
        ]),
        KeyBindingViewModel('audio_next', 'Nächster Titel', 'keySpace', 'nächstes stück', ['next']),
        KeyBindingViewModel('audio_rewind', 'Titel Zurückspulen', 'keySpace', 'titel zurückspulen', ['rewind']),
        KeyBindingViewModel('audio_forward', 'Vorwärts-track', 'keySpace', 'vorwärts verfolgen', ['forward']),
        KeyBindingViewModel('audio_repeat', 'Titel Wiederholen', 'keySpace', 'titel wiederholen', ['repeat']),
        KeyBindingViewModel(
            'audio_random',
            'Zufallswiedergabe Von Titeln',
            'keySpace',
            'zufallswiedergabe von titeln',
            ['shuffle', 'random']
        ),
        KeyBindingViewModel('gamepad_button_1', 'Controller-taste 1 (a)', 'joystick', 'taste eins', []),
        KeyBindingViewModel('gamepad_button_2', 'Controller-taste 2 (b)', 'joystick', 'taste zwei', []),
        KeyBindingViewModel('gamepad_button_3', 'Controller-taste 3 (x)', 'joystick', 'taste drei', []),
        KeyBindingViewModel('gamepad_button_4', 'Controller-taste 4 (y)', 'joystick', 'taste vier', []),
        KeyBindingViewModel('gamepad_button_5', 'Controller-taste 5 (lb)', 'joystick', 'taste fünf', []),
        KeyBindingViewModel('gamepad_button_6', 'Controller-taste 6 (rb)', 'joystick', 'taste sechs', []),
        KeyBindingViewModel('gamepad_button_7', 'Controller-taste 7 (lt)', 'joystick', 'taste sieben', []),
        KeyBindingViewModel('gamepad_button_8', 'Controller-taste 8 (rt)', 'joystick', 'taste acht', []),
        KeyBindingViewModel('gamepad_button_9', 'Controller-taste 9 (optionen)', 'joystick', 'taste neun', []),
        KeyBindingViewModel('gamepad_button_10', 'Controller-taste 10 (pause)', 'joystick', 'taste zehn', []),
        KeyBindingViewModel('gamepad_button_11', 'Controller-taste 11 (l3)', 'joystick', 'taste elf', []),
        KeyBindingViewModel('gamepad_button_12', 'Controller-taste 12 (r3)', 'joystick', 'taste zwölf', []),
        KeyBindingViewModel('gamepad_button_13', 'Controller D-pad Nach Oben', 'joystick', 'richtung nach oben', []),
        KeyBindingViewModel('gamepad_button_14', 'Controller D-pad Nach Unten', 'joystick', 'richtung nach unten', []),
        KeyBindingViewModel('gamepad_button_15', 'Controller D-pad Links', 'joystick', 'richtung links', []),
        KeyBindingViewModel('gamepad_button_16', 'Controller D-pad Rechts', 'joystick', 'richtung rechts', []),
    ],
    ja: [
        KeyBindingViewModel('mouse_button_1', 'マウスボタン1（左クリック）', 'mouseLeftClick', 'クリック', [
            'click',
            'left click',
        ]),
        KeyBindingViewModel('mouse_button_2', 'マウスボタン2（右クリック）', 'mouseRightClick', '右クリック', []),
        KeyBindingViewModel('mouse_button_3', 'マウスボタン3（中央クリック）', 'mouseMiddleClick', '中クリック', []),

        KeyBindingViewModel('1', '1', 'key1', 'いち', []),
        KeyBindingViewModel('2', '2', 'key2', 'に', []),
        KeyBindingViewModel('3', '3', 'key3', 'さん', []),
        KeyBindingViewModel('4', '4', 'key4', 'よん', []),
        KeyBindingViewModel('5', '5', 'key5', 'ご', []),
        KeyBindingViewModel('6', '6', 'key6', 'ろく', []),
        KeyBindingViewModel('7', '7', 'key7', 'なな', []),
        KeyBindingViewModel('8', '8', 'key8', 'はち', []),
        KeyBindingViewModel('9', '9', 'key9', 'きゅう', []),
        KeyBindingViewModel('0', '0', 'key0', 'ぜろ', []),

        KeyBindingViewModel('a', 'A', 'keyA', 'えー', []),
        KeyBindingViewModel('b', 'B', 'keyB', 'びー', []),
        KeyBindingViewModel('c', 'C', 'keyC', 'しー', []),
        KeyBindingViewModel('d', 'D', 'keyD', 'でぃー', []),
        KeyBindingViewModel('e', 'E', 'keyE', 'いー', []),
        KeyBindingViewModel('f', 'F', 'keyF', 'えふ', []),
        KeyBindingViewModel('g', 'G', 'keyG', 'じー', []),
        KeyBindingViewModel('h', 'H', 'keyH', 'えいち', []),
        KeyBindingViewModel('i', 'I', 'keyI', 'あい', []),
        KeyBindingViewModel('j', 'J', 'keyJ', 'じぇー', []),
        KeyBindingViewModel('k', 'K', 'keyK', 'けー', []),
        KeyBindingViewModel('l', 'L', 'keyL', 'える', []),
        KeyBindingViewModel('m', 'M', 'keyM', 'えむ', []),
        KeyBindingViewModel('n', 'N', 'keyN', 'えぬ', []),
        KeyBindingViewModel('o', 'O', 'keyO', 'おー', []),
        KeyBindingViewModel('p', 'P', 'keyP', 'ぴー', []),
        KeyBindingViewModel('q', 'Q', 'keyQ', 'きゅー', []),
        KeyBindingViewModel('r', 'R', 'keyR', 'あーる', []),
        KeyBindingViewModel('s', 'S', 'keyS', 'えす', []),
        KeyBindingViewModel('t', 'T', 'keyT', 'てぃー', []),
        KeyBindingViewModel('u', 'U', 'keyU', 'ゆー', []),
        KeyBindingViewModel('v', 'V', 'keyV', 'ぶい', []),
        KeyBindingViewModel('w', 'W', 'keyW', 'だぶりゅー', []),
        KeyBindingViewModel('x', 'X', 'keyX', 'えっくす', []),
        KeyBindingViewModel('y', 'Y', 'keyY', 'わい', []),
        KeyBindingViewModel('z', 'Z', 'keyZ', 'ぜっと', []),

        KeyBindingViewModel('_', '_', 'keySpace', 'アンダースコア', []),
        KeyBindingViewModel('-', '-', 'keySpace', 'ダッシュ', ['subtract']),
        KeyBindingViewModel(',', '、', 'keySpace', 'コンマ', ['comma', 'Comma (,)', ',']),
        KeyBindingViewModel(';', ';', 'keySpace', 'セミコロン', []),
        KeyBindingViewModel(':', ':', 'keySpace', 'コリン', []),
        KeyBindingViewModel('?', '?', 'keySpace', 'はてな', []),
        KeyBindingViewModel('.', '.', 'keyDot', 'ピリオド', []),
        KeyBindingViewModel('"', '"', 'keySpace', 'クォーテーション', []),
        KeyBindingViewModel('(', '（', 'keySpace', 'かっこ', []),
        KeyBindingViewModel(')', '）', 'keySpace', '閉じるかっこ', []),
        KeyBindingViewModel('[', '[', 'keySpace', '開くブラケット', []),
        KeyBindingViewModel(']', ']', 'keySpace', '閉じるブラケット', []),
        KeyBindingViewModel('{', '{', 'keySpace', '開くカーリーブラケット', []),
        KeyBindingViewModel('}', '}', 'keySpace', '閉じるカーリーブラケット', []),
        KeyBindingViewModel('!', '！', 'keySpace', 'びっくり', []),
        KeyBindingViewModel('@', '@', 'keySpace', 'アットマーク', []),
        KeyBindingViewModel('*', '*', 'keyAsterisk', 'アスタリスク', ['multiply']),
        KeyBindingViewModel('/', '/', 'keySolidus', 'スラッシュ', ['divide']),
        KeyBindingViewModel('\\', '\\', 'keySpace', 'バックスラッシュ', []),
        KeyBindingViewModel('&', '＆', 'keySpace', 'アンパサンド', []),
        KeyBindingViewModel('#', '#', 'keyHashtag', 'シャープ', []),
        KeyBindingViewModel('%', '%', 'keySpace', 'パーセント', []),
        KeyBindingViewModel('`', '`', 'keySpace', 'バッククォート', []),
        KeyBindingViewModel('^', '^', 'keySpace', 'キャレット', []),
        KeyBindingViewModel('+', '+', 'keySpace', 'プラス', ['add']),
        KeyBindingViewModel('<', '<', 'keyLeftAngleParentheses', 'より小さい', []),
        KeyBindingViewModel('=', '=', 'keyEqual', 'イコール', []),
        KeyBindingViewModel('>', '>', 'keyRightAngleParentheses', 'より大きい', []),
        KeyBindingViewModel('|', '|', 'keySpace', 'パイプ', []),
        KeyBindingViewModel('~', '〜', 'keySpace', 'チルダ', []),
        KeyBindingViewModel('$', '$', 'keySpace', 'ドル記号', []),
        KeyBindingViewModel("'", "'", 'keySpace', 'ドル', []),

        KeyBindingViewModel('backspace', 'バックスペース', 'keyDelete', 'バックスペース', ['back space', 'backspace']),
        KeyBindingViewModel('capslock', 'キャプスロック', 'keyCapsLock', 'キャップスロック', ['caps lock', 'caps']),
        KeyBindingViewModel('numlock', 'ナムロック', 'keySpace', 'numロック', ['num lock']),
        KeyBindingViewModel(
            'scrolllock',
            'スクロールロック',
            'keySpace',
            'スクロールロック',
            ['scroll lock'],
            'Windows'
        ),
        KeyBindingViewModel('cmd', 'コマンド', 'keyCommand', 'コマンド', ['leftcmd', 'command'], 'MacOS'),
        KeyBindingViewModel('rightcmd', '右コマンド', 'keyCommand', '右コマンド', [], 'MacOS'),
        KeyBindingViewModel('delete', 'デリート', 'keyDelete', 'デリート', []),
        KeyBindingViewModel('down', 'した', 'keyPageDownButton', 'した', ['down arrow', 'downarrow']),
        KeyBindingViewModel('left', 'ひだり', 'keyHomeButton', 'ひだり', ['left arrow', 'leftarrow']),
        KeyBindingViewModel('right', 'みぎ', 'keyEndButton', 'みぎ', ['right arrow', 'rightarrow']),
        KeyBindingViewModel('up', 'うえ', 'keyPageUpButton', 'うえ', ['up arrow', 'uparrow']),
        KeyBindingViewModel('enter', '入力', 'keyEnterMac', '入力', ['return']),
        KeyBindingViewModel('escape', 'エスケープ', 'keyEsc', 'エスケープ', ['esc']),
        KeyBindingViewModel('leftalt', '左オルト', 'keyAlt', '左オルト', ['alt'], 'Windows'),
        KeyBindingViewModel('leftcontrol', '左コントロール', 'keyCtrl', '左コントロール', ['ctrl', 'control']),
        KeyBindingViewModel('leftshift', '左シフト', 'shift', '左シフト', ['shift']),
        KeyBindingViewModel('option', 'オプション', 'keyOption', 'オプション', ['opt'], 'MacOS'),
        KeyBindingViewModel('rightcontrol', '右コントロール', 'keyCtrl', '右コントロール', []),
        KeyBindingViewModel('rightshift', '右シフト', 'shift', '右シフト', []),
        KeyBindingViewModel('space', 'スペース', 'keySpace', 'スペース', ['space bar', 'spacebar']),
        KeyBindingViewModel('tab', 'タブ', 'keyTab', 'タブ', []),
        KeyBindingViewModel('win', 'ウィンドウズ', 'keyWindows', 'ウィンドウズ', ['windows'], 'Windows'),
        KeyBindingViewModel('fn', 'ファンクション', 'keyFn', 'ファンクション', ['function', 'fn'], 'MacOS'),
        KeyBindingViewModel('menu', 'メニュー', 'keySpace', 'メニュー', []),
        KeyBindingViewModel(
            'printscreen',
            'プリントスクリーン',
            'keySpace',
            'プリントスクリーン',
            ['print screen'],
            'Windows'
        ),
        KeyBindingViewModel('insert', 'インサート', 'keySpace', 'インサート', []),
        KeyBindingViewModel('home', 'ホーム', 'keySpace', 'ホーム', [], 'Windows'),
        KeyBindingViewModel('end', 'エンド', 'keySpace', 'エンド', [], 'Windows'),
        KeyBindingViewModel('pageup', 'ページアップ', 'keySpace', 'ページアップ', ['page up']),
        KeyBindingViewModel('pagedown', 'ページダウン', 'keySpace', 'ページダウン', ['page down']),

        KeyBindingViewModel('numpad_0', 'テンキー0', 'key0', 'テンキーゼロ', []),
        KeyBindingViewModel('numpad_1', 'テンキー1', 'key1', 'テンキーいち', []),
        KeyBindingViewModel('numpad_2', 'テンキー2', 'key2', 'テンキーに', []),
        KeyBindingViewModel('numpad_3', 'テンキー3', 'key3', 'テンキーさん', []),
        KeyBindingViewModel('numpad_4', 'テンキー4', 'key4', 'テンキーよん', []),
        KeyBindingViewModel('numpad_5', 'テンキー5', 'key5', 'テンキーご', []),
        KeyBindingViewModel('numpad_6', 'テンキー6', 'key6', 'テンキーろく', []),
        KeyBindingViewModel('numpad_7', 'テンキー7', 'key7', 'テンキーなな', []),
        KeyBindingViewModel('numpad_8', 'テンキー8', 'key8', 'テンキーはち', []),
        KeyBindingViewModel('numpad_9', 'テンキー9', 'key9', 'テンキーきゅう', []),
        KeyBindingViewModel('numpad_decimal', 'テンキー小数点', 'keyDot', 'テンキーしょうすうてん', []),

        KeyBindingViewModel('f1', 'F1', 'keyF1', 'エフワン', []),
        KeyBindingViewModel('f2', 'F2', 'keyF2', 'エフツー', []),
        KeyBindingViewModel('f3', 'F3', 'keyF3', 'エフスリー', []),
        KeyBindingViewModel('f4', 'F4', 'keyF4', 'エフフォー', []),
        KeyBindingViewModel('f5', 'F5', 'keyF5', 'エフファイブ', []),
        KeyBindingViewModel('f6', 'F6', 'keyF6', 'エフシックス', []),
        KeyBindingViewModel('f7', 'F7', 'keyF7', 'エフセブン', []),
        KeyBindingViewModel('f8', 'F8', 'keyF8', 'エフエイト', []),
        KeyBindingViewModel('f9', 'F9', 'keyF9', 'エフナイン', []),
        KeyBindingViewModel('f10', 'F10', 'keyF10', 'エフテン', []),
        KeyBindingViewModel('f11', 'F11', 'keyF11', 'エフイレブン', []),
        KeyBindingViewModel('f12', 'F12', 'keyF12', 'エフトゥエルブ', []),

        KeyBindingViewModel('audio_mute', '音声をミュートする', 'keySpace', 'ミュート', [
            'mute',
            'mute audio',
            'audio mute',
        ]),
        KeyBindingViewModel('audio_vol_down', '音量を下げる', 'keySpace', 'ボリュームダウン', ['volume down']),
        KeyBindingViewModel('audio_vol_up', '音量を上げる', 'keySpace', 'ボリュームアップ', ['volume up']),
        KeyBindingViewModel('audio_play', 'オーディオを再生', 'keySpace', '再生', ['play']),
        KeyBindingViewModel('audio_stop', '音声を停止', 'keySpace', '停止', ['stop']),
        KeyBindingViewModel('audio_pause', '音声を一時停止', 'keySpace', '音声を一時停止', ['pause']),
        KeyBindingViewModel('audio_prev', '前のトラック', 'keySpace', '前のトラック', ['previous', 'audio_prev']),
        KeyBindingViewModel('audio_next', '次のトラック', 'keySpace', '次のトラック', ['next']),
        KeyBindingViewModel('audio_rewind', 'トラックを巻き戻す', 'keySpace', '巻き戻し', ['rewind']),
        KeyBindingViewModel('audio_forward', '前進軌道', 'keySpace', '早送り', ['forward']),
        KeyBindingViewModel('audio_repeat', 'トラックを繰り返す', 'keySpace', 'リピート', ['repeat']),
        KeyBindingViewModel('audio_random', 'トラックをシャッフル', 'keySpace', 'シャッフル', ['shuffle', 'random']),

        KeyBindingViewModel('gamepad_button_1', 'コントローラーボタン1 (a)', 'joystick', 'ボタンワン', []),
        KeyBindingViewModel('gamepad_button_2', 'コントローラーボタン2 (b)', 'joystick', 'ボタンツー', []),
        KeyBindingViewModel('gamepad_button_3', 'コントローラーボタン3 (x)', 'joystick', 'ボタンスリー', []),
        KeyBindingViewModel('gamepad_button_4', 'コントローラーボタン4 (y)', 'joystick', 'ボタンフォー', []),
        KeyBindingViewModel('gamepad_button_5', 'コントローラーボタン5（lb）', 'joystick', 'ボタンファイブ', []),
        KeyBindingViewModel('gamepad_button_6', 'コントローラーボタン6 (rb)', 'joystick', 'ボタンシックス', []),
        KeyBindingViewModel('gamepad_button_7', 'コントローラーボタン7 (lt)', 'joystick', 'ボタンセブン', []),
        KeyBindingViewModel('gamepad_button_8', 'コントローラーボタン8 (rt)', 'joystick', 'ボタンエイト', []),
        KeyBindingViewModel('gamepad_button_9', 'コントローラーボタン9（オプション）', 'joystick', 'ボタンナイン', []),
        KeyBindingViewModel('gamepad_button_10', 'コントローラーボタン10（一時停止）', 'joystick', 'ボタンテン', []),
        KeyBindingViewModel('gamepad_button_11', 'コントローラーボタン11（l3）', 'joystick', 'ボタンイレブン', []),
        KeyBindingViewModel('gamepad_button_12', 'コントローラーボタン12（r3）', 'joystick', 'ボタントゥエルブ', []),
        KeyBindingViewModel('gamepad_button_13', 'コントローラーの十字ボタン上', 'joystick', '上ボタン', []),
        KeyBindingViewModel('gamepad_button_14', 'コントローラーのdパッドを下', 'joystick', '下ボタン', []),
        KeyBindingViewModel('gamepad_button_15', 'コントローラー Dパッド 左', 'joystick', '左ボタン', []),
        KeyBindingViewModel('gamepad_button_16', 'コントローラー Dパッド右', 'joystick', '右ボタン', []),
    ],
};

export const getDefaultKeyMapping = (locale: string, platform?: OSTypes): KeyCommandDisplayMapping[] => {
    const language = getSupportedLocale(locale, 'display');
    let result: KeyCommandDisplayMapping[] = [];
    if (language) {
        result = defaultKeyMappings[language as DisplayLocales] ?? [];
    }

    if (platform && platform !== 'generic') {
        // filter by platform. generic profiles get all of the keybindings
        result = result.filter((kcd) => !kcd.platform || kcd.platform === 'generic' || kcd.platform === platform);
    }

    return result;
};

export const getKeyDisplayname = (key: string, locale: string) => {
    const keyName = getDefaultKeyMapping(locale).find((v) => v.key == key)?.displayName ?? key;
    if (keyName?.length == 1) return keyName.toUpperCase();
    return keyName;
};

export const getKeyIcon = (key: string, locale: string) => {
    const newKey = getKeyValue(key, locale);
    const keyIcon = getDefaultKeyMapping(locale).find((v) => v.key == newKey)?.icon ?? newKey;
    return keyIcon as IconName;
};

export const getKeyValue = (value: string, locale: string) => {
    const defaultKeyMapping = getDefaultKeyMapping(locale);
    const key = defaultKeyMapping.find((v) => v.key == value)?.key;
    if (key) {
        return key;
    }

    const keyFromDisplayName = defaultKeyMapping.find((v) => v.displayName == value)?.key;
    if (keyFromDisplayName) {
        return keyFromDisplayName;
    }

    const keyFromAlias = defaultKeyMapping.find((v) => v.alternateKeys?.includes(value))?.key;
    if (keyFromAlias) {
        return keyFromAlias;
    }

    return value;
};

export const getKeyPlatform = (key: string, locale: string) => {
    const newKey = getKeyValue(key, locale);
    const keyPlatform: OSTypes = getDefaultKeyMapping(locale).find((v) => v.key == newKey)?.platform ?? 'generic';
    return keyPlatform;
};
